import { useLayoutEffect, useRef, useState, type RefObject } from "react";

/**
 * How many fixed-height rows fit in `heightPx` — all of them if they fit,
 * otherwise as many as fit above a "+N more" line of `morePx`.
 *
 * Dashboard lists use this to fill the height their row gives them instead of
 * a fixed few over empty space. Rows must have a fixed height for it to be
 * exact, which is why it is arithmetic rather than per-row measurement.
 */
export function itemsThatFit(
  heightPx: number | null,
  total: number,
  { rowPx, morePx, beforeMeasure }: { rowPx: number; morePx: number; beforeMeasure: number }
): number {
  if (total <= 0) return 0;
  if (heightPx == null || heightPx <= 0) return Math.min(total, beforeMeasure);
  if (total * rowPx <= heightPx) return total;
  // Always list at least one: a list that names none is only a count.
  return Math.max(1, Math.floor((heightPx - morePx) / rowPx));
}

/**
 * Pixels per `rem` on this page. Tailwind heights are rem-based and the app
 * scales its root font (14.4px, not 16), so a row's pixel height must be
 * derived, never written down as `h-10 = 40px`. 16 where there is no
 * document or no computed size (tests).
 */
export function remPx(): number {
  if (typeof document === "undefined") return 16;
  const size = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(size) && size > 0 ? size : 16;
}

/**
 * The live height of an element, via ResizeObserver; null until measured (and
 * always null where ResizeObserver does not exist).
 *
 * Measure an element whose height the LAYOUT decides — e.g. an absolutely
 * positioned box filling its parent — never one sized by the rows inside it,
 * or adding a row grows the box and the count chases its own tail.
 */
export function useMeasuredHeight<T extends HTMLElement>(): [RefObject<T>, number | null] {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      setHeight(entries[0]?.contentRect.height ?? null);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, height];
}
